import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { GroupItemCategoriesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsGroupsSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as GroupItemTemplateCategoryActions from "../../../actions/groupItemTemplateCategory";
import * as NavigationActions from "../../../actions/navigation";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { GroupSettingsLayout } from "../../index";

import "./GroupItemTemplateCategorysPage.scss";

interface GroupItemTemplateCategorysPageProps {
    formFilter: IFormFilter;
    groupItemTemplateCategory: Dtos.GroupItemTemplateCategory[];
    loadingGroupItemTemplateCategorys: boolean;
    loadGroupItemTemplateCategorysSuccess: boolean;
    loadGroupItemTemplateCategorysFailure: boolean;
    loadGroupItemTemplateCategorysErrorCode: string;
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearGroupItemTemplateCategorys: GroupItemTemplateCategoryActions.IGroupItemTemplateCategoryClearActionCreator;
    loadGroupItemTemplateCategorys: GroupItemTemplateCategoryActions.IGroupItemTemplateCategoryLoadActionCreator;
    filter: GroupItemTemplateCategoryActions.IGroupItemTemplateCategoryFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const GroupItemTemplateCategorysPage: FC<
    GroupItemTemplateCategorysPageProps
> = ({
    formFilter,
    groupItemTemplateCategory,
    loadingGroupItemTemplateCategorys,
    loadGroupItemTemplateCategorysSuccess,
    loadGroupItemTemplateCategorysFailure,
    loadGroupItemTemplateCategorysErrorCode,
    permissions,
    clearGroupItemTemplateCategorys,
    loadGroupItemTemplateCategorys,
    filter,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showGroupItemTemplateCategorys =
        !loadingGroupItemTemplateCategorys &&
        loadGroupItemTemplateCategorysSuccess;


    // Load group item template categorys
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsGroupsSubMenu(permissions));
            setBreadcrumbs(GroupItemCategoriesBreadcrumbs());

            loadGroupItemTemplateCategorys();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsGroupsSubMenu,
        permissions,
        setBreadcrumbs,
        GroupItemCategoriesBreadcrumbs,
        loadGroupItemTemplateCategorys,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadGroupItemTemplateCategorysFailure) {
            navigate(`/error/${loadGroupItemTemplateCategorysErrorCode}`);
            clearGroupItemTemplateCategorys();
        }
    }, [
        loadGroupItemTemplateCategorysFailure,
        loadGroupItemTemplateCategorysErrorCode,
        navigate,
        clearGroupItemTemplateCategorys,
    ]);

    // Cleanup effect to clear the group item template categorys on component unmount
    useEffect(() => {
        return () => {
            clearGroupItemTemplateCategorys();
        };
    }, [clearGroupItemTemplateCategorys]);

    const createTable = useMemo(() => {
        if (
            !groupItemTemplateCategory ||
            groupItemTemplateCategory.length == 0
        ) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no group item template
                            categories to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination =
            groupItemTemplateCategory.length > DEFAULT_PAGE_SIZE;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={groupItemTemplateCategory}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination
                            ? DEFAULT_PAGE_SIZE
                            : groupItemTemplateCategory.length
                    }
                    pageSize={
                        showPagination
                            ? undefined
                            : groupItemTemplateCategory.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no group ite template categories to display"
                    resizable={false}
                    getTdProps={(
                        state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (e, handleOriginal) => {
                                const url: string =
                                    "/settings/group/itemcategories/" +
                                    rowInfo.original.id;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [
        groupItemTemplateCategory,
        PAGE_SIZE_OPTIONS,
        DEFAULT_PAGE_SIZE,
        navigate,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Group Item Categories</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.GroupItemCategoriesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/settings/group/itemcategories/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showGroupItemTemplateCategorys ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showGroupItemTemplateCategorys ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        formFilter,
        filter,
        permissions,
        showGroupItemTemplateCategorys,
        createTable,
    ]);

    return (
        <GroupSettingsLayout
            loading={loadingGroupItemTemplateCategorys}
            permission={Dtos.Permission.GroupItemCategoriesView}
        >
            {renderContent}
        </GroupSettingsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<GroupItemTemplateCategorysPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formFilter: state.groupItemTemplateCategorys.formFilter,
        groupItemTemplateCategory:
            state.groupItemTemplateCategorys.formFilteredData,
        loadingGroupItemTemplateCategorys:
            state.groupItemTemplateCategorys.loadState &&
            state.groupItemTemplateCategorys.loadState.status ==
                RequestState.Pending,
        loadGroupItemTemplateCategorysSuccess:
            state.groupItemTemplateCategorys.loadState &&
            state.groupItemTemplateCategorys.loadState.status ==
                RequestState.Success,
        loadGroupItemTemplateCategorysFailure:
            state.groupItemTemplateCategorys.loadState &&
            state.groupItemTemplateCategorys.loadState.status ==
                RequestState.Failure,
        loadGroupItemTemplateCategorysErrorCode:
            state.groupItemTemplateCategorys.loadState.errorCode,
        user: state.user.data,
        permissions: permissions,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<GroupItemTemplateCategorysPageProps> => {
    return {
        loadGroupItemTemplateCategorys: bindActionCreators(
            GroupItemTemplateCategoryActions.LoadGroupItemTemplateCategory,
            dispatch
        ),
        clearGroupItemTemplateCategorys: bindActionCreators(
            GroupItemTemplateCategoryActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(
            GroupItemTemplateCategoryActions.Filter,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(GroupItemTemplateCategorysPage);
